import type { AdminMe, SupervisorMe } from '@neon-one/auctions-types';

import { setCookie, getCookie, deleteCookie } from './ssoCookies';

/**
 * Parent-domain session cookies — the single source of truth for every admin
 * origin (bare domain + all org subdomains). Replaces the old per-origin
 * localStorage copies, whose drift caused stale sessions to survive logout
 * on other subdomains.
 *
 *   na_session         — the active identity (org admin, or a supervisor
 *                        assumed into an org).
 *   na_supervisor_root — the supervisor's own identity, kept while they are
 *                        assumed into an org so the lobby round-trip works.
 *
 * Cookies are JS-readable by design (the SPA attaches the JWT itself) —
 * the same exposure class as the localStorage they replace. This is a
 * documented security exception: see
 * docs/architecture/decisions/ADR-010-admin-session-parent-domain-cookies.md
 * for the threat model and the HttpOnly revisit path. Expiry is enforced
 * by the JWT's own exp on read (adminSession store), the cookie max-age
 * is just a ceiling.
 */
const SESSION_COOKIE = 'na_session';
const SUPERVISOR_ROOT_COOKIE = 'na_supervisor_root';
const MAX_AGE_SECONDS = 12 * 60 * 60;

export interface SessionCookie {
  jwt: string;
  user: AdminMe | null;
}

export interface SupervisorRootCookie {
  jwt: string;
  supervisorUser: SupervisorMe;
}

function readJson<T>(name: string): T | null {
  const raw = getCookie(name);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as T;
  } catch {
    deleteCookie(name);
    return null;
  }
}

export function readSessionCookie(): SessionCookie | null {
  return readJson<SessionCookie>(SESSION_COOKIE);
}

export function writeSessionCookie(s: SessionCookie): void {
  setCookie(SESSION_COOKIE, JSON.stringify(s), MAX_AGE_SECONDS);
}

export function clearSessionCookie(): void {
  deleteCookie(SESSION_COOKIE);
}

export function readSupervisorRootCookie(): SupervisorRootCookie | null {
  return readJson<SupervisorRootCookie>(SUPERVISOR_ROOT_COOKIE);
}

export function writeSupervisorRootCookie(s: SupervisorRootCookie): void {
  setCookie(SUPERVISOR_ROOT_COOKIE, JSON.stringify(s), MAX_AGE_SECONDS);
}

export function clearSupervisorRootCookie(): void {
  deleteCookie(SUPERVISOR_ROOT_COOKIE);
}
